// Aba "Indicações" do painel Tecnologia — fila da PWA pública /indica?t=<token>.
// Duas sub-visões: a fila de indicações recebidas (com mudança de etapa) e a
// gestão dos links compartilháveis.
// Backend: server/routes/indica.cjs (rotas /admin, authMiddleware).
// Sem polling: a fila muda por ação humana — carrega ao montar e no botão de
// atualizar, mesmo padrão de DeployPanel/BackupPanel.
import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  FiRefreshCw, FiLink, FiCopy, FiCheck, FiSlash, FiPlus, FiExternalLink, FiLoader,
  FiTrendingUp, FiClock, FiCheckCircle, FiUsers, FiSearch, FiEdit2,
} from 'react-icons/fi'
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, Legend,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../lib/api'
import { formatPhone, dataHoraCurta } from '../lib/mascaras'
import { AXIS, GRID, CURSOR } from '../lib/chartTheme'

type Status = 'enviado' | 'analisando' | 'concluido' | 'recusado'

// Espelha lib/indica-rules.cjs no backend. Duplicação consciente e pequena: a
// alternativa seria uma rota só pra buscar 4 rótulos. Se divergir, vale o do
// backend (gravado como snapshot em indica_events.label).
//
// As cores são PALETA DE STATUS, não categórica: cada uma carrega significado
// (informativo / em curso / bom / recusado). Validadas com o validador da skill
// dataviz contra a superfície neu #e6e9f0 — passam nos 6 checks, incluindo
// separação para daltonismo (pior par ΔE 10.2 deutan) e contraste ≥ 3:1.
// Nunca aparecem sozinhas: sempre acompanhadas do rótulo em texto.
const ETAPAS: { id: Status; label: string; cor: string; texto: string }[] = [
  { id: 'enviado', label: 'Enviado', cor: '#3b82f6', texto: 'text-blue-600' },
  { id: 'analisando', label: 'Analisando', cor: '#b45309', texto: 'text-amber-700' },
  { id: 'concluido', label: 'Concluído', cor: '#0d9488', texto: 'text-teal-700' },
  { id: 'recusado', label: 'Não aprovado', cor: '#e11d48', texto: 'text-rose-600' },
]
const TRANSICOES: Record<Status, Status[]> = {
  enviado: ['analisando', 'concluido', 'recusado'],
  analisando: ['enviado', 'concluido', 'recusado'],
  concluido: ['analisando'],
  recusado: ['analisando'],
}
const FINAIS: Status[] = ['concluido', 'recusado']
const etapa = (id: string) => ETAPAS.find((e) => e.id === id)

interface Referral {
  id: string; nome: string; phone: string; cpf: string
  produto: string; produto_label: string | null
  status: Status; status_label: string
  created_at: string; last_event_at: string; eventos: number
  link: { id: string; label: string; owner_name: string | null }
}
interface Evento {
  id: number; type: string; label: string; message: string | null
  status_from: string | null; status_to: string | null
  visible_to_client: number; actor_name: string | null; created_at: string
}
interface LinkRow {
  id: string; label: string; owner_name: string | null; owner_phone: string | null
  token: string; url: string; active: boolean
  hits: number; last_hit_at: string | null
  total: number; pendentes: number; created_at: string
  por_status?: Record<string, number>
}
interface Stats {
  por_status: Record<string, number>
  novas_hoje: number; concluidas_mes: number; total: number
  serie_14d?: { dia: string; n: number }[]
}
type Fetch = (url: string, init?: RequestInit) => Promise<Response>

export function IndicacoesPanel() {
  const { authenticatedFetch, user } = useAuth()
  const [aba, setAba] = useState<'fila' | 'desempenho' | 'links'>('fila')

  return (
    <div className="h-full min-h-0 flex flex-col gap-4">
      <div className="flex shrink-0">
        <div className="neu-seg rounded-full p-1 flex gap-1 mx-auto">
          {([['fila', 'Indicações'], ['desempenho', 'Desempenho'], ['links', 'Links']] as const).map(([id, rotulo]) => (
            <button
              key={id}
              onClick={() => setAba(id)}
              className={`px-5 py-1.5 rounded-full text-[13px] font-medium transition-colors ${
                aba === id ? 'neu-relief bg-primary text-primary-foreground' : 'neu-seg-btn text-muted-foreground'
              }`}
            >
              {rotulo}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0">
        {aba === 'fila' && <Fila authenticatedFetch={authenticatedFetch} />}
        {aba === 'desempenho' && <Desempenho authenticatedFetch={authenticatedFetch} isAdmin={!!user?.isAdmin} />}
        {aba === 'links' && <Links authenticatedFetch={authenticatedFetch} isAdmin={!!user?.isAdmin} />}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Fila
// ─────────────────────────────────────────────────────────────────────────────
function Fila({ authenticatedFetch }: { authenticatedFetch: Fetch }) {
  const [itens, setItens] = useState<Referral[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [filtro, setFiltro] = useState<'' | Status>('')
  const [busca, setBusca] = useState('')
  const [aberto, setAberto] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      const qs = new URLSearchParams()
      if (filtro) qs.set('status', filtro)
      if (busca.trim()) qs.set('q', busca.trim())
      const [r1, r2] = await Promise.all([
        authenticatedFetch(`${API_ENDPOINTS.INDICA_ADMIN_REFERRALS}?${qs}`),
        authenticatedFetch(API_ENDPOINTS.INDICA_ADMIN_STATS),
      ])
      if (!r1.ok) throw new Error(r1.status === 403 ? 'Sem permissão.' : `Erro ${r1.status}`)
      const d1 = await r1.json()
      setItens(d1.referrals || [])
      if (r2.ok) setStats(await r2.json())
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar.')
    } finally {
      setCarregando(false)
    }
  }, [authenticatedFetch, filtro, busca])

  useEffect(() => { carregar() }, [filtro])          // eslint-disable-line react-hooks/exhaustive-deps

  // Otimista COM rollback: aplica na hora e, se o servidor recusar, volta ao
  // valor anterior e mostra o erro na própria linha — um toast que some
  // deixaria o operador achando que salvou.
  const mudarStatus = useCallback(async (id: string, novo: Status, anterior: Status) => {
    setItens((p) => p.map((i) => (i.id === id ? { ...i, status: novo, status_label: etapa(novo)!.label } : i)))
    try {
      const r = await authenticatedFetch(`${API_ENDPOINTS.INDICA_ADMIN_REFERRALS}/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: novo }),
      })
      if (!r.ok) throw new Error(`Erro ${r.status}`)
      carregar()
      return null
    } catch (e) {
      setItens((p) => p.map((i) => (i.id === id ? { ...i, status: anterior, status_label: etapa(anterior)!.label } : i)))
      return e instanceof Error ? e.message : 'Falha ao salvar.'
    }
  }, [authenticatedFetch, carregar])

  return (
    <div className="h-full min-h-0 flex flex-col gap-4">
      {/* Faixa de KPIs em cima, gráfico em largura total embaixo. Lado a lado,
          a coluna dos tiles terminava bem antes do cartão do gráfico e deixava
          um vazio de ~170px; e a série de 14 dias ganha com a largura inteira. */}
      {stats && (
        <div className="flex flex-col gap-3 shrink-0">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            <Tile icone={<FiTrendingUp />} rotulo="novas hoje" valor={stats.novas_hoje} cor="#3b82f6" />
            <Tile icone={<FiClock />} rotulo="em análise" valor={stats.por_status?.analisando || 0} cor="#b45309" />
            <Tile icone={<FiCheckCircle />} rotulo="concluídas no mês" valor={stats.concluidas_mes} cor="#0d9488" />
            <Tile icone={<FiUsers />} rotulo="no total" valor={stats.total} />
          </div>
          <Graficos stats={stats} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 shrink-0">
        <div className="neu-seg rounded-full p-1 flex gap-1">
          {([['', 'Todas'], ...ETAPAS.map((e) => [e.id, e.label])] as [string, string][]).map(([id, rotulo]) => (
            <button
              key={id || 'todas'}
              onClick={() => setFiltro(id as '' | Status)}
              className={`px-3.5 py-1.5 rounded-full text-[12px] font-medium ${
                filtro === id ? 'neu-seg-btn neu-on text-foreground' : 'neu-seg-btn text-muted-foreground'
              }`}
            >
              {rotulo}
            </button>
          ))}
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); carregar() }}
          className="neu-inset rounded-full px-3.5 flex items-center gap-2 flex-1 min-w-[180px] max-w-[320px]"
        >
          <FiSearch className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Nome, telefone ou CPF…"
            className="w-full bg-transparent border-0 outline-none py-2 text-[13px] text-foreground"
          />
        </form>

        <button
          onClick={carregar}
          className="neu-item rounded-full w-9 h-9 flex items-center justify-center shrink-0"
          title="Atualizar"
        >
          <FiRefreshCw className={`w-4 h-4 text-muted-foreground ${carregando ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {erro && <p className="neu-inset rounded-xl px-4 py-2.5 text-[13px] text-rose-600 shrink-0">{erro}</p>}

      <div className="flex-1 min-h-0 overflow-y-auto">
        {carregando && !itens.length ? (
          <p className="text-[13px] text-muted-foreground py-8 text-center">Carregando…</p>
        ) : !itens.length ? (
          <p className="text-[13px] text-muted-foreground py-8 text-center">Nenhuma indicação recebida.</p>
        ) : (
          // POÇO, não relevo. Uma lista de dados é conteúdo recuado NA
          // superfície, não um bloco flutuando acima dela — e sem contorno o
          // alto relevo fica sem leitura. No neu-table-well o próprio inset é
          // o contorno. O gutter de 8px (que a classe já impõe) também mata a
          // cunha branca que aparecia no canto: com overflow-hidden, o fundo
          // reto da linha zebrada era cortado pelo raio do container.
          <ul className="neu-table-well">
            {itens.map((r, i) => (
              <LinhaReferral
                key={r.id}
                r={r}
                primeira={i === 0}
                ultima={i === itens.length - 1}
                aberto={aberto === r.id}
                onAlternar={() => setAberto(aberto === r.id ? null : r.id)}
                onMudarStatus={mudarStatus}
                authenticatedFetch={authenticatedFetch}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

/** Tile de KPI. Layout em linha (número à esquerda, rótulo à direita) em vez de
 *  empilhado: os valores aqui são de 1 a 2 dígitos, e um número de 30px sobre
 *  duas linhas deixava o card alto demais para o que ele informa — sobrava ar e
 *  a fileira competia com o gráfico ao lado. */
function Tile({ icone, rotulo, valor, cor }: {
  icone: React.ReactNode; rotulo: string; valor: number; cor?: string
}) {
  return (
    <div className="neu-card rounded-xl px-3.5 py-3 flex items-center gap-2.5">
      <span className="w-3.5 h-3.5 shrink-0 flex items-center justify-center" style={{ color: cor }}>
        {icone}
      </span>
      {/* Rótulo ocupa o vão e o número ancora na direita: nestes cards largos,
          tudo alinhado à esquerda deixava metade do cartão vazia. */}
      <span className="text-[11.5px] leading-tight text-muted-foreground flex-1 min-w-0 truncate">
        {rotulo}
      </span>
      <span className="text-[20px] font-semibold leading-none tabular-nums text-foreground shrink-0">
        {valor}
      </span>
    </div>
  )
}

/** Tendência (14 dias) + composição por etapa. */
function Graficos({ stats }: { stats: Stats }) {
  const serie = stats.serie_14d || []
  const totalStatus = ETAPAS.reduce((a, e) => a + (stats.por_status?.[e.id] || 0), 0)

  return (
    <div className="neu-card rounded-2xl p-4 flex flex-col gap-3">
      <span className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
        Últimos 14 dias
      </span>

      {/* Série única → sem legenda: o título já nomeia o que está no gráfico. */}
      <div className="h-[118px] -ml-2">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={serie} margin={{ top: 4, right: 6, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="grad-indica" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0d9488" stopOpacity={0.28} />
                <stop offset="100%" stopColor="#0d9488" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={GRID} strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="dia"
              tick={{ fontSize: 9, fill: AXIS }}
              tickFormatter={(d: string) => d.slice(8, 10)}
              axisLine={false} tickLine={false} interval={2}
            />
            <YAxis
              tick={{ fontSize: 9, fill: AXIS }}
              axisLine={false} tickLine={false} width={22} allowDecimals={false}
            />
            <Tooltip
              cursor={{ stroke: CURSOR, strokeWidth: 24 }}
              contentStyle={{ fontSize: 12, borderRadius: 10, border: 'none' }}
              wrapperClassName="neu-tip"
              labelFormatter={(d) => {
                const [a, m, dd] = String(d).split('-')
                return `${dd}/${m}/${a}`
              }}
              formatter={(v) => {
                const n = Number(v)
                return [`${n} indicaç${n === 1 ? 'ão' : 'ões'}`, ''] as [string, string]
              }}
            />
            <Area
              type="monotone" dataKey="n"
              stroke="#0d9488" strokeWidth={2}
              fill="url(#grad-indica)"
              dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Composição por etapa: barra empilhada única, com 2px de respiro entre
          segmentos e rótulo direto em texto — a cor nunca carrega o significado
          sozinha. */}
      {totalStatus > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex gap-[2px] h-2.5">
            {ETAPAS.map((e) => {
              const n = stats.por_status?.[e.id] || 0
              if (!n) return null
              return (
                <span
                  key={e.id}
                  className="h-full first:rounded-l-full last:rounded-r-full"
                  style={{ background: e.cor, width: `${(n / totalStatus) * 100}%` }}
                  title={`${e.label}: ${n}`}
                />
              )
            })}
          </div>
          <div className="flex flex-wrap gap-x-3.5 gap-y-1">
            {ETAPAS.map((e) => {
              const n = stats.por_status?.[e.id] || 0
              if (!n) return null
              return (
                <span key={e.id} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: e.cor }} />
                  {e.label} <strong className="text-foreground tabular-nums">{n}</strong>
                </span>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

/** Mini passo-a-passo na linha da fila: mostra onde a indicação está sem
 *  precisar abrir. Recusado não entra no trilho — é saída, não estágio. */
function MiniStepper({ status }: { status: Status }) {
  const trilha: Status[] = ['enviado', 'analisando', 'concluido']
  const recusado = status === 'recusado'
  const idx = recusado ? 1 : Math.max(0, trilha.indexOf(status))
  return (
    <span className="flex items-center gap-[3px] shrink-0" aria-hidden="true">
      {trilha.map((s, i) => (
        <span
          key={s}
          className="rounded-full"
          style={{
            width: i === idx ? 14 : 6, height: 6,
            background: i <= idx ? (recusado && i === 1 ? '#e11d48' : etapa(s)!.cor) : 'var(--neu-dark, #c4c9d6)',
            transition: 'width 240ms ease',
          }}
        />
      ))}
    </span>
  )
}

/** Campo do formulário de link. Altura fixa nos três para o botão da última
 *  coluna alinhar pelo mesmo baseline em qualquer quebra. */
function CampoLink({
  label, valor, onChange, placeholder, inputMode,
}: {
  label: string; valor: string; onChange: (v: string) => void
  placeholder?: string; inputMode?: 'text' | 'tel'
}) {
  return (
    <label className="min-w-0">
      <span className="block text-[11px] text-muted-foreground mb-1 truncate">{label}</span>
      <span className="neu-inset block rounded-lg px-3">
        <input
          value={valor}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          inputMode={inputMode}
          className="w-full bg-transparent border-0 outline-none h-[34px] text-[13px] text-foreground"
        />
      </span>
    </label>
  )
}

/** Centavos → R$. Intl com currency já põe o símbolo e o separador certos. */
function brl(centavos: number): string {
  return (Number(centavos || 0) / 100).toLocaleString('pt-BR', {
    style: 'currency', currency: 'BRL', minimumFractionDigits: 2,
  })
}
/** Versão curta pro eixo do gráfico: "R$ 1,2 mil". Eixo com valor cheio
 *  empurra a área de plotagem e vira parede de dígitos. */
function brlCurto(centavos: number): string {
  const v = Number(centavos || 0) / 100
  if (Math.abs(v) >= 1000) return `${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`
  return v.toLocaleString('pt-BR', { maximumFractionDigits: 0 })
}

function Iniciais({ nome }: { nome: string }) {
  const p = String(nome || '').trim().split(/\s+/).filter(Boolean)
  const txt = !p.length ? '?' : p.length === 1 ? p[0].slice(0, 2) : p[0][0] + p[p.length - 1][0]
  return (
    <span className="neu-inset rounded-full w-9 h-9 shrink-0 flex items-center justify-center
                     text-[12px] font-bold text-muted-foreground uppercase">
      {txt}
    </span>
  )
}

function LinhaReferral({
  r, aberto, primeira, ultima, onAlternar, onMudarStatus, authenticatedFetch,
}: {
  r: Referral
  aberto: boolean
  primeira: boolean
  ultima: boolean
  onAlternar: () => void
  onMudarStatus: (id: string, novo: Status, anterior: Status) => Promise<string | null>
  authenticatedFetch: Fetch
}) {
  // busy POR ID (não global): um busy global travaria a tabela inteira
  // enquanto uma linha salva.
  const [busy, setBusy] = useState(false)
  const [erroLinha, setErroLinha] = useState<string | null>(null)
  const [confirmando, setConfirmando] = useState<Status | null>(null)
  const [timeline, setTimeline] = useState<Evento[] | null>(null)
  const [cpfVisivel, setCpfVisivel] = useState(false)

  useEffect(() => {
    if (!aberto || timeline) return
    authenticatedFetch(`${API_ENDPOINTS.INDICA_ADMIN_REFERRALS}/${r.id}`)
      .then((x) => (x.ok ? x.json() : null))
      .then((d) => setTimeline(d?.timeline || []))
      .catch(() => setTimeline([]))
  }, [aberto, timeline, r.id, authenticatedFetch])

  async function aplicar(novo: Status) {
    if (busy) return                 // guard de reentrada: duplo clique é a norma
    setBusy(true)
    setErroLinha(null)
    const err = await onMudarStatus(r.id, novo, r.status)
    if (err) setErroLinha(err)
    else setTimeline(null)           // força recarregar a timeline
    setConfirmando(null)
    setBusy(false)
  }

  function pedir(novo: Status) {
    // Só as etapas finais pedem confirmação: são as que a PWA anuncia ao
    // indicador como desfecho.
    if (FINAIS.includes(novo)) setConfirmando(novo)
    else aplicar(novo)
  }

  const e = etapa(r.status)
  const permitidas = TRANSICOES[r.status] || []

  return (
    // neu-tr dá a zebra e o hover (só background-color); neu-rule-2 dá o fio
    // de 1px (só box-shadow). Propriedades diferentes, então convivem — o slot
    // de box-shadow do NEU é único e duas regras que o disputam se anulam.
    <li className={`neu-tr overflow-hidden ${primeira ? 'rounded-t-lg' : ''} ${ultima ? 'rounded-b-lg' : ''} ${
      aberto ? 'neu-tr-on' : ''} ${ultima ? '' : 'neu-rule-2'}`}>
      <div className="px-4 py-3 flex items-center gap-3 flex-wrap cursor-pointer">
        <Iniciais nome={r.nome} />

        <button onClick={onAlternar} className="flex-1 min-w-[160px] text-left">
          <p className="text-[14px] font-semibold text-foreground truncate">{r.nome}</p>
          <p className="text-[11px] text-muted-foreground truncate">
            {formatPhone(r.phone.replace(/^55/, ''))} · {r.produto_label || r.produto} ·{' '}
            {r.link.owner_name || r.link.label}
          </p>
        </button>

        <MiniStepper status={r.status} />
        <span className={`neu-tag ${e?.texto || ''} text-[11px] font-semibold px-2 py-1 shrink-0`}>
          {r.status_label}
        </span>
        <span className="text-[11px] text-muted-foreground tabular-nums shrink-0 w-[74px] text-right">
          {dataHoraCurta(r.created_at)}
        </span>
      </div>

      {aberto && (
        <div className="px-4 pb-4">
          <hr className="neu-hr border-0 mb-3" />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <p className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Mudar etapa
              </p>

              {confirmando ? (
                <div className="neu-inset rounded-xl px-3 py-2.5 flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] text-foreground flex-1">
                    Confirmar “{etapa(confirmando)!.label}”?
                  </span>
                  <button
                    onClick={() => aplicar(confirmando)}
                    disabled={busy}
                    className="neu-relief bg-primary text-primary-foreground rounded-lg px-3 py-1.5 text-[12px] font-semibold disabled:opacity-60"
                  >
                    {busy ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : 'Sim'}
                  </button>
                  <button
                    onClick={() => setConfirmando(null)}
                    className="neu-item rounded-lg px-3 py-1.5 text-[12px] text-muted-foreground"
                  >
                    Não
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {ETAPAS.map((et) => {
                    const atual = et.id === r.status
                    const pode = permitidas.includes(et.id)
                    return (
                      <button
                        key={et.id}
                        onClick={() => pedir(et.id)}
                        // Transições inválidas ficam ESMAECIDAS, não sumidas: o
                        // operador precisa entender que existem.
                        disabled={atual || !pode || busy}
                        title={atual ? 'Etapa atual' : pode ? '' : `Não é possível ir de ${r.status_label} para ${et.label}`}
                        className={`rounded-lg px-3 py-2 text-[12px] font-medium flex items-center gap-1.5 ${
                          atual
                            ? 'neu-seg-btn neu-on text-foreground'   // afundado = selecionado
                            : pode
                              ? 'neu-item text-foreground'
                              : 'neu-item text-muted-foreground/40 cursor-not-allowed'
                        }`}
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full shrink-0"
                          style={{ background: atual || pode ? et.cor : 'transparent' }}
                        />
                        {et.label}
                      </button>
                    )
                  })}
                </div>
              )}

              {erroLinha && <p className="text-[12px] text-rose-600 mt-2">{erroLinha}</p>}

              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 mt-4 text-[12px]">
                <dt className="text-muted-foreground">CPF</dt>
                <dd className="text-foreground tabular-nums">
                  {cpfVisivel ? r.cpf : `***.${r.cpf.slice(3, 6)}.${r.cpf.slice(6, 9)}-**`}
                  <button
                    onClick={() => setCpfVisivel((v) => !v)}
                    className="ml-2 text-[11px] text-primary underline"
                  >
                    {cpfVisivel ? 'ocultar' : 'ver'}
                  </button>
                </dd>
                <dt className="text-muted-foreground">Link</dt>
                <dd className="text-foreground">{r.link.label}</dd>
              </dl>
            </div>

            <div>
              <p className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Histórico
              </p>
              {!timeline ? (
                <p className="text-[12px] text-muted-foreground">Carregando…</p>
              ) : (
                <ol className="flex flex-col gap-2">
                  {timeline.map((ev) => (
                    <li key={ev.id} className="text-[12px]">
                      <div className="flex items-baseline gap-2">
                        <span
                          className="w-1.5 h-1.5 rounded-full shrink-0 translate-y-[-1px]"
                          style={{ background: etapa(ev.status_to || '')?.cor || 'var(--neu-dark)' }}
                        />
                        <span className="text-foreground font-medium">{ev.label}</span>
                        {!ev.visible_to_client && (
                          <span className="neu-tag text-slate-500 text-[10px] px-1.5 py-0.5">interno</span>
                        )}
                        <span className="text-muted-foreground tabular-nums ml-auto shrink-0">
                          {dataHoraCurta(ev.created_at)}
                        </span>
                      </div>
                      {ev.message && <p className="text-muted-foreground mt-0.5 pl-3.5">{ev.message}</p>}
                      {ev.actor_name && <p className="text-muted-foreground/70 text-[11px] pl-3.5">por {ev.actor_name}</p>}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      )}
    </li>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Desempenho — gráficos e ganhos
// ─────────────────────────────────────────────────────────────────────────────
interface LinhaDesempenho {
  id: string; label: string; owner_name: string | null
  realizado: number; previsto: number; total: number; concluidas: number
}
interface Desemp {
  valores: Record<string, number>
  resumo: {
    realizado: number; previsto: number; indicacoes: number; concluidas: number
    conversao: number | null; decididas: number; ticket_medio: number
  }
  meses: { mes: string; realizado: number; previsto: number; total: number; concluidas: number }[]
  produtos: { id: string; label: string; valor: number; realizado: number; previsto: number; total: number; concluidas: number }[]
  indicadores: LinhaDesempenho[]
}

const MES_CURTO = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
const rotuloMes = (m: string) => {
  const [a, mm] = String(m).split('-')
  return `${MES_CURTO[Number(mm) - 1] || mm}/${String(a).slice(2)}`
}

function Desempenho({ authenticatedFetch, isAdmin }: { authenticatedFetch: Fetch; isAdmin: boolean }) {
  const [d, setD] = useState<Desemp | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [editando, setEditando] = useState(false)

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.INDICA_ADMIN_DESEMPENHO)
      if (!r.ok) throw new Error(r.status === 403 ? 'Sem permissão.' : `Erro ${r.status}`)
      setD(await r.json())
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar.')
    } finally { setCarregando(false) }
  }, [authenticatedFetch])

  useEffect(() => { carregar() }, [carregar])

  if (carregando && !d) return <p className="text-[13px] text-muted-foreground py-10 text-center">Carregando…</p>
  if (erro) return <p className="neu-inset rounded-xl px-4 py-2.5 text-[13px] text-rose-600">{erro}</p>
  if (!d) return null

  const semDados = d.resumo.indicacoes === 0

  return (
    <div className="h-full min-h-0 overflow-y-auto flex flex-col gap-4 pr-1">
      {/* Hero: o número que o diretor procura primeiro. Não é gráfico — é um
          valor único, e valor único não vira gráfico. */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-3 shrink-0">
        <div className="neu-card rounded-2xl p-5 flex flex-col gap-1">
          <span className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
            Ganhos realizados
          </span>
          <span className="text-[38px] leading-none font-semibold tabular-nums" style={{ color: '#0d9488' }}>
            {brl(d.resumo.realizado)}
          </span>
          <span className="text-[12px] text-muted-foreground mt-1">
            {d.resumo.concluidas} indicaç{d.resumo.concluidas === 1 ? 'ão concluída' : 'ões concluídas'}
            {d.resumo.previsto > 0 && <> · <strong className="text-foreground">{brl(d.resumo.previsto)}</strong> em andamento</>}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <MiniKpi rotulo="ticket médio" valor={brl(d.resumo.ticket_medio)} />
          <MiniKpi
            rotulo="taxa de aprovação"
            /* null ≠ 0: sem nada decidido ainda, "0%" mentiria. */
            valor={d.resumo.conversao == null ? '—' : `${d.resumo.conversao}%`}
            nota={d.resumo.conversao == null ? 'nada decidido ainda' : `de ${d.resumo.decididas} decididas`}
          />
          <MiniKpi rotulo="indicações" valor={String(d.resumo.indicacoes)} />
          <MiniKpi rotulo="em andamento" valor={brl(d.resumo.previsto)} />
        </div>
      </div>

      {semDados ? (
        <p className="neu-card rounded-2xl px-5 py-10 text-center text-[13px] text-muted-foreground">
          Ainda não há indicações para montar os gráficos.
        </p>
      ) : (
        <>
          {/* Evolução mensal. Barras (não linha): meses são períodos discretos,
              e uma linha entre eles sugere valores intermediários que não existem. */}
          <Cartao titulo="Ganhos por mês" nota="realizado × em andamento">
            <div className="h-[190px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.meses} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke={GRID} strokeDasharray="2 4" vertical={false} />
                  <XAxis dataKey="mes" tickFormatter={rotuloMes} tick={{ fontSize: 10, fill: AXIS }}
                         axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v) => brlCurto(Number(v))} tick={{ fontSize: 10, fill: AXIS }}
                         axisLine={false} tickLine={false} width={44} />
                  <Tooltip
                    cursor={{ fill: CURSOR }}
                    wrapperClassName="neu-tip"
                    contentStyle={{ fontSize: 12, borderRadius: 10, border: 'none' }}
                    labelFormatter={(m) => rotuloMes(String(m))}
                    formatter={(v, n) => [brl(Number(v)), n === 'realizado' ? 'Realizado' : 'Em andamento']}
                  />
                  <Legend
                    verticalAlign="top" align="right" height={22} iconType="circle" iconSize={8}
                    formatter={(n) => (
                      <span style={{ fontSize: 11, color: 'hsl(var(--muted-foreground))' }}>
                        {n === 'realizado' ? 'Realizado' : 'Em andamento'}
                      </span>
                    )}
                  />
                  {/* 2px de respiro entre as barras empilhadas: sem o gap, dois
                      tons próximos viram um bloco só. */}
                  <Bar dataKey="realizado" stackId="g" fill="#0d9488" maxBarSize={54} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="previsto" stackId="g" fill="#b45309" maxBarSize={54} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Cartao>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {/* Uma medida (ganho) através de categorias → UMA cor. Cores
                diferentes por barra sugeririam que a cor significa algo. */}
            <Cartao titulo="Ganho por produto" nota="realizado">
              <BarrasHorizontais
                itens={d.produtos.map((p) => ({
                  chave: p.id,
                  rotulo: p.label,
                  valor: p.realizado,
                  nota: `${p.concluidas}/${p.total} · ${brl(p.valor)} cada`,
                }))}
              />
            </Cartao>

            <Cartao titulo="Ranking de indicadores" nota="por ganho realizado">
              <BarrasHorizontais
                itens={d.indicadores.slice(0, 6).map((l) => ({
                  chave: l.id,
                  rotulo: l.owner_name || l.label,
                  valor: l.realizado,
                  nota: `${l.concluidas}/${l.total} concluídas`,
                }))}
                vazio="Nenhum indicador com ganho ainda."
              />
            </Cartao>
          </div>
        </>
      )}

      <Cartao
        titulo="Comissão por produto"
        nota={isAdmin ? 'clique para editar' : 'somente leitura'}
        acao={isAdmin ? (
          <button
            onClick={() => setEditando((v) => !v)}
            className="neu-item rounded-lg px-3 py-1.5 text-[12px] flex items-center gap-1.5"
          >
            <FiEdit2 className="w-3.5 h-3.5" /> {editando ? 'Fechar' : 'Editar'}
          </button>
        ) : undefined}
      >
        {editando && isAdmin
          ? <EditorValores valores={d.valores} produtos={d.produtos}
                           authenticatedFetch={authenticatedFetch}
                           onSalvo={() => { setEditando(false); carregar() }} />
          : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              {d.produtos.map((p) => (
                <div key={p.id} className="neu-well rounded-xl px-3 py-2.5">
                  <p className="text-[11px] text-muted-foreground truncate">{p.label}</p>
                  <p className="text-[16px] font-semibold tabular-nums text-foreground mt-0.5">{brl(p.valor)}</p>
                </div>
              ))}
            </div>
          )}
      </Cartao>
    </div>
  )
}

function Cartao({ titulo, nota, acao, children }: {
  titulo: string; nota?: string; acao?: React.ReactNode; children: React.ReactNode
}) {
  return (
    <div className="neu-card rounded-2xl p-4 flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <span className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">{titulo}</span>
        {nota && <span className="text-[10.5px] text-muted-foreground/70">· {nota}</span>}
        {acao && <span className="ml-auto">{acao}</span>}
      </div>
      {children}
    </div>
  )
}

function MiniKpi({ rotulo, valor, nota }: { rotulo: string; valor: string; nota?: string }) {
  return (
    <div className="neu-card rounded-xl px-3 py-2.5 flex flex-col justify-center">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">{rotulo}</span>
      <span className="text-[17px] font-semibold tabular-nums text-foreground leading-tight mt-0.5">{valor}</span>
      {nota && <span className="text-[10px] text-muted-foreground/70 truncate">{nota}</span>}
    </div>
  )
}

/**
 * Barras horizontais em HTML, não em recharts: são poucas categorias com
 * rótulo longo, e o eixo Y do recharts trunca nome de pessoa. Aqui o rótulo
 * fica acima da barra e sempre cabe. Uma medida → uma cor.
 */
function BarrasHorizontais({ itens, vazio }: {
  itens: { chave: string; rotulo: string; valor: number; nota?: string }[]
  vazio?: string
}) {
  const max = Math.max(1, ...itens.map((i) => i.valor))
  const temAlgum = itens.some((i) => i.valor > 0)
  if (!temAlgum) {
    return <p className="text-[12px] text-muted-foreground py-3">{vazio || 'Sem ganho registrado ainda.'}</p>
  }
  return (
    <ul className="flex flex-col gap-2.5">
      {itens.map((i) => (
        <li key={i.chave} className="flex flex-col gap-1">
          <div className="flex items-baseline gap-2">
            <span className="text-[12px] text-foreground truncate flex-1 min-w-0">{i.rotulo}</span>
            <span className="text-[12px] font-semibold tabular-nums text-foreground shrink-0">{brl(i.valor)}</span>
          </div>
          {/* neu-gauge dá o canal gravado do design system ao trilho. */}
          <div className="h-2 rounded-full overflow-hidden neu-gauge">
            <div
              className="h-full rounded-full transition-[width] duration-500"
              style={{ width: `${(i.valor / max) * 100}%`, background: '#0d9488' }}
            />
          </div>
          {i.nota && <span className="text-[10.5px] text-muted-foreground">{i.nota}</span>}
        </li>
      ))}
    </ul>
  )
}

function EditorValores({ valores, produtos, authenticatedFetch, onSalvo }: {
  valores: Record<string, number>
  produtos: { id: string; label: string }[]
  authenticatedFetch: Fetch
  onSalvo: () => void
}) {
  // Editado em REAIS (o operador pensa em reais), convertido pra centavos só
  // no envio — a API é sempre centavos inteiros.
  const [reais, setReais] = useState<Record<string, string>>(() =>
    Object.fromEntries(produtos.map((p) => [p.id, ((valores[p.id] || 0) / 100).toFixed(2).replace('.', ',')]))
  )
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  async function salvar() {
    if (salvando) return
    setSalvando(true); setErro(null)
    try {
      const centavos: Record<string, number> = {}
      for (const p of produtos) {
        const n = Number(String(reais[p.id] || '0').replace(/\./g, '').replace(',', '.'))
        if (!Number.isFinite(n) || n < 0) throw new Error(`Valor inválido em ${p.label}.`)
        centavos[p.id] = Math.round(n * 100)
      }
      const r = await authenticatedFetch(API_ENDPOINTS.INDICA_ADMIN_VALORES, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ valores: centavos }),
      })
      if (!r.ok) throw new Error(`Erro ${r.status}`)
      onSalvo()
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao salvar.')
    } finally { setSalvando(false) }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        {produtos.map((p) => (
          <label key={p.id} className="min-w-0">
            <span className="block text-[11px] text-muted-foreground mb-1 truncate">{p.label}</span>
            <span className="neu-inset flex items-center gap-1 rounded-lg px-3">
              <span className="text-[12px] text-muted-foreground shrink-0">R$</span>
              <input
                value={reais[p.id] ?? ''}
                onChange={(e) => setReais((v) => ({ ...v, [p.id]: e.target.value.replace(/[^\d,.]/g, '') }))}
                inputMode="decimal"
                className="w-full bg-transparent border-0 outline-none h-[34px] text-[13px] text-foreground tabular-nums"
              />
            </span>
          </label>
        ))}
      </div>
      {erro && <p className="text-[12px] text-rose-600">{erro}</p>}
      <div className="flex items-center gap-2">
        <button
          onClick={salvar}
          disabled={salvando}
          className="neu-relief bg-primary text-primary-foreground rounded-lg px-4 py-2 text-[13px] font-semibold
                     flex items-center gap-1.5 disabled:opacity-50"
        >
          {salvando ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiCheck className="w-3.5 h-3.5" />}
          Salvar
        </button>
        <span className="text-[11px] text-muted-foreground">
          Vale para o cálculo de todos os ganhos, inclusive os já registrados.
        </span>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Links
// ─────────────────────────────────────────────────────────────────────────────
function Links({ authenticatedFetch, isAdmin }: { authenticatedFetch: Fetch; isAdmin: boolean }) {
  const [links, setLinks] = useState<LinkRow[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [criando, setCriando] = useState(false)
  const [filtroEtapa, setFiltroEtapa] = useState<'' | Status>('')
  const [novoLabel, setNovoLabel] = useState('')
  const [novoNome, setNovoNome] = useState('')
  const [novoTel, setNovoTel] = useState('')
  const [copiado, setCopiado] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.INDICA_ADMIN_LINKS)
      if (!r.ok) throw new Error(r.status === 403 ? 'Sem permissão.' : `Erro ${r.status}`)
      const d = await r.json()
      setLinks(d.links || [])
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar.')
    } finally {
      setCarregando(false)
    }
  }, [authenticatedFetch])

  useEffect(() => { carregar() }, [carregar])

  // Filtrar por etapa aqui, e não no servidor: são poucos links e o filtro
  // precisa reagir na hora. Um link entra se tiver ao menos uma indicação
  // naquela etapa — e o contador exibido passa a ser o daquela etapa.
  const visiveis = useMemo(() => (
    !filtroEtapa ? links : links.filter((l) => (l.por_status?.[filtroEtapa] || 0) > 0)
  ), [links, filtroEtapa])

  const contagemEtapa = useMemo(() => {
    const c: Record<string, number> = {}
    for (const e of ETAPAS) c[e.id] = links.filter((l) => (l.por_status?.[e.id] || 0) > 0).length
    return c
  }, [links])

  const resumo = useMemo(() => ({
    ativos: visiveis.filter((l) => l.active).length,
    indicacoes: visiveis.reduce((a, l) => a + l.total, 0),
    aberturas: visiveis.reduce((a, l) => a + l.hits, 0),
  }), [visiveis])

  async function criar(ev: React.FormEvent) {
    ev.preventDefault()
    if (criando || novoLabel.trim().length < 2) return
    setCriando(true)
    setErro(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.INDICA_ADMIN_LINKS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: novoLabel.trim(),
          owner_name: novoNome.trim() || undefined,
          owner_phone: novoTel.replace(/\D/g, '') || undefined,
        }),
      })
      if (!r.ok) throw new Error(`Erro ${r.status}`)
      setNovoLabel(''); setNovoNome(''); setNovoTel('')
      carregar()
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao criar o link.')
    } finally {
      setCriando(false)
    }
  }

  // Revogar, nunca deletar: apagar o link orfanaria as indicações já feitas.
  async function revogar(l: LinkRow) {
    try {
      const r = await authenticatedFetch(`${API_ENDPOINTS.INDICA_ADMIN_LINKS}/${l.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !l.active }),
      })
      if (!r.ok) throw new Error(`Erro ${r.status}`)
      carregar()
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao alterar o link.')
    }
  }

  async function copiar(url: string, id: string) {
    try {
      await navigator.clipboard.writeText(url)
      setCopiado(id)
      window.setTimeout(() => setCopiado(null), 2000)
    } catch {
      // clipboard falha em contexto não-seguro ou sem permissão: mostra o link
      // pro usuário copiar à mão em vez de não fazer nada.
      window.prompt('Copie o link:', url)
    }
  }

  return (
    <div className="h-full min-h-0 flex flex-col gap-3">
      {links.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 shrink-0">
          <Tile icone={<FiLink />} rotulo="links ativos" valor={resumo.ativos} cor="#0d9488" />
          <Tile icone={<FiUsers />} rotulo="indicações geradas" valor={resumo.indicacoes} cor="#3b82f6" />
          <Tile icone={<FiTrendingUp />} rotulo="aberturas do app" valor={resumo.aberturas} />
        </div>
      )}

      {/* Grid com colunas declaradas, não flex-wrap: com min-width nos campos e
          um botão de largura própria, a quebra de linha deixava um vão grande à
          direita e o botão descolado dos rótulos. Aqui as colunas colapsam em
          pontos previsíveis (1 → 2 → 4). */}
      {isAdmin && (
        <form
          onSubmit={criar}
          className="neu-bar rounded-2xl p-3 shrink-0
                     grid gap-2.5 items-end
                     grid-cols-1 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1.1fr)_9.5rem_auto]"
        >
          <CampoLink label="Nome do link" valor={novoLabel} onChange={setNovoLabel}
                     placeholder="Ex.: Consultoras Dourados" />
          <CampoLink label="Indicador (opcional)" valor={novoNome} onChange={setNovoNome}
                     placeholder="Nome de quem indica" />
          <CampoLink label="WhatsApp (opcional)" valor={novoTel}
                     onChange={(v) => setNovoTel(formatPhone(v))}
                     placeholder="(67) 99999-8888" inputMode="tel" />
          <button
            type="submit"
            disabled={criando || novoLabel.trim().length < 2}
            className="neu-relief bg-primary text-primary-foreground rounded-lg h-[34px] px-4 text-[13px] font-semibold
                       flex items-center justify-center gap-1.5 disabled:opacity-50 sm:col-span-2 lg:col-span-1"
          >
            {criando ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiPlus className="w-3.5 h-3.5" />}
            Gerar link
          </button>
        </form>
      )}

      {erro && <p className="neu-inset rounded-xl px-4 py-2.5 text-[13px] text-rose-600 shrink-0">{erro}</p>}

      {links.length > 0 && (
        <div className="neu-seg rounded-full p-1 flex gap-1 self-start shrink-0 flex-wrap">
          {([['', 'Todos'], ...ETAPAS.map((e) => [e.id, e.label])] as [string, string][]).map(([id, rotulo]) => {
            const n = id ? contagemEtapa[id] || 0 : links.length
            return (
              <button
                key={id || 'todos'}
                onClick={() => setFiltroEtapa(id as '' | Status)}
                disabled={n === 0}
                title={id ? `Links com indicação em "${rotulo}"` : 'Todos os links'}
                className={`px-3.5 py-1.5 rounded-full text-[12px] font-medium flex items-center gap-1.5 ${
                  filtroEtapa === id ? 'neu-seg-btn neu-on text-foreground'
                    : n === 0 ? 'neu-seg-btn text-muted-foreground/35 cursor-not-allowed'
                    : 'neu-seg-btn text-muted-foreground'
                }`}
              >
                {id && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: etapa(id)?.cor }} />}
                {rotulo}
                <span className="tabular-nums opacity-60">{n}</span>
              </button>
            )
          })}
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto">
        {carregando && !links.length ? (
          <p className="text-[13px] text-muted-foreground py-8 text-center">Carregando…</p>
        ) : !links.length ? (
          <p className="text-[13px] text-muted-foreground py-8 text-center">Nenhum link criado ainda.</p>
        ) : !visiveis.length ? (
          <p className="text-[13px] text-muted-foreground py-8 text-center">
            Nenhum link com indicação nesta etapa.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {visiveis.map((l) => (
              <li key={l.id} className="neu-card rounded-2xl px-4 py-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="neu-inset rounded-lg w-8 h-8 flex items-center justify-center shrink-0">
                    <FiLink className="w-3.5 h-3.5 text-muted-foreground" />
                  </span>
                  <div className="flex-1 min-w-[160px]">
                    <p className="text-[14px] font-semibold text-foreground truncate">{l.label}</p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {l.owner_name || 'sem indicador'} · {l.total} indicaç{l.total === 1 ? 'ão' : 'ões'}
                      {l.pendentes > 0 && ` · ${l.pendentes} em aberto`} · {l.hits} abertura{l.hits === 1 ? '' : 's'}
                    </p>
                  </div>
                  <span className={`neu-tag ${l.active ? 'text-teal-700' : 'text-slate-500'} text-[11px] font-semibold px-2 py-1 shrink-0`}>
                    {l.active ? 'ativo' : 'revogado'}
                  </span>
                </div>

                {/* Composição por etapa do link. Barra + rótulos em texto: a
                    cor nunca carrega o significado sozinha. */}
                {l.total > 0 && (
                  <div className="mt-2.5 flex items-center gap-x-3 gap-y-1 flex-wrap">
                    <span className="flex gap-[2px] h-1.5 w-[104px] shrink-0">
                      {ETAPAS.map((e) => {
                        const n = l.por_status?.[e.id] || 0
                        if (!n) return null
                        return (
                          <span
                            key={e.id}
                            className="h-full first:rounded-l-full last:rounded-r-full"
                            style={{ background: e.cor, width: `${(n / l.total) * 100}%` }}
                            title={`${e.label}: ${n}`}
                          />
                        )
                      })}
                    </span>
                    {ETAPAS.map((e) => {
                      const n = l.por_status?.[e.id] || 0
                      if (!n) return null
                      return (
                        <span key={e.id} className="flex items-center gap-1 text-[10.5px] text-muted-foreground">
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: e.cor }} />
                          {e.label} <strong className="text-foreground tabular-nums">{n}</strong>
                        </span>
                      )
                    })}
                  </div>
                )}

                <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                  <code className="neu-inset rounded-lg px-3 py-1.5 text-[11px] font-mono text-muted-foreground
                                   flex-1 min-w-[200px] truncate">
                    {l.url}
                  </code>
                  <button
                    onClick={() => copiar(l.url, l.id)}
                    className="neu-item rounded-lg px-3 py-1.5 text-[12px] flex items-center gap-1.5 shrink-0"
                    title="Copiar link"
                  >
                    {copiado === l.id
                      ? <><FiCheck className="w-3.5 h-3.5 text-teal-600" /> Copiado</>
                      : <><FiCopy className="w-3.5 h-3.5" /> Copiar</>}
                  </button>
                  {l.owner_phone && (
                    <a
                      href={`https://wa.me/${l.owner_phone}?text=${encodeURIComponent(`Olá! Este é o seu link de indicações: ${l.url}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="neu-item rounded-lg px-3 py-1.5 text-[12px] flex items-center gap-1.5 shrink-0"
                    >
                      <FiExternalLink className="w-3.5 h-3.5" /> WhatsApp
                    </a>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => revogar(l)}
                      className={`neu-item rounded-lg px-3 py-1.5 text-[12px] flex items-center gap-1.5 shrink-0 ${
                        l.active ? 'text-rose-600' : 'text-teal-700'
                      }`}
                    >
                      <FiSlash className="w-3.5 h-3.5" /> {l.active ? 'Revogar' : 'Reativar'}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
